import React, { useState, useMemo } from 'react';
import { CircularProgressOverlay } from '@campfire/circular-progress-overlay';
import { formatDistance, unpackToDateTime } from '@campfire/hot-date';
import { Grid, IconButton, Typography, Box } from '@material-ui/core';
import { Delete as DeleteIcon, KeyboardArrowLeft, Redo } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import { TabletButton } from '@campfire/tablet-button';
import { StatusChip } from '../../../../common/chips/StatusChip';
import { getIncomingStatusChips, isAwaitingScreeningReview } from '../common/incoming-volunteer-status';
import { ConfirmationDialog } from '../../../../common/dialogs/ConfirmationDialog';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { FallbackAvatar } from '../../../general/user-profile/components/Avatar';
import { IncomingVolunteersApplicationTaskSection } from './components/incoming-volunteer-task-list-sections/IncomingVolunteersApplicationTaskSection';
import { IncomingVolunteersInviteTaskSection } from './components/incoming-volunteer-task-list-sections/IncomingVolunteersInviteTaskSection';
import { IncomingVolunteerTasksSection } from './components/incoming-volunteer-task-list-sections/IncomingVolunteerTasksSection';
import { MatchingPanel } from './components/matching/MatchingPanel';
import { useMatchingRules } from './components/matching/use-matching-rules';
import { TaskSideMenuBar } from './components/side-menu-content/TaskSideMenuBar';
import { IncomingVolunteersLeftColumnWrapper } from './components/wrappers/IncomingVolunteersLeftColumnWrapper';
import { IncomingVolunteersPageWrapper } from './components/wrappers/IncomingVolunteersPageWrapper';
import { IncomingVolunteersRightColumnWrapper } from './components/wrappers/IncomingVolunteersRightColumnWrapper';
import { useIncomingVolunteerActions } from './incoming-volunteer-action.gql';
import { GET_INCOMING_VOLUNTEER_APPLICATION_DETAIL, LeftMenuItemType } from './incoming-volunteer-model.gql';
import { useVolunteersContext } from '../VolunteersContext';
import { CommentsList } from '../common/CommentsList';
import { BypassDialog } from './BypassDialog';
import { FunnelIndicators } from './components/FunnelIndicators';
import {
  GetIncomingVolunteerApplicationDetail,
  GetIncomingVolunteerApplicationDetailVariables,
} from './__generated__/GetIncomingVolunteerApplicationDetail';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { CommunicateButton } from '../../volunteer-common-profile/profile-sections/action-menu/CommunicateButton';
import { ApplicantCommunicationsSection } from './components/ApplicantCommunicationsSection';
import { ExpandableSidePanel } from './components/ExpandableSidePanel';

const useStyles = () => {
  const { theme, isMobile } = useCampfireTheme();
  return makeStyles({
    listItem: {
      padding: isMobile ? '16px 8px' : '16px 48px',
      borderBottom: `1px solid ${theme.color.grey.border}`,
    },
    iconContainer: {
      marginRight: 8,
    },
    canvas: {
      backgroundColor: theme.color.grey.background,
      padding: isMobile ? '10px 8px' : '16px 20px',
      flexGrow: 1,
      flexShrink: 0,
      minWidth: 0,
      minHeight: '100%',
      boxSizing: 'border-box',
      display: 'flex',
    },
    card: {
      backgroundColor: '#ffffff',
      border: `1px solid ${theme.color.grey.border}`,
      borderRadius: 10,
      overflow: 'hidden',
      padding: '6px 0 2px',
      height: '100%',
      boxSizing: 'border-box',
    },
    taskMenuBarWrapper: {
      borderLeft: `1px solid ${theme.color.grey.border}`,
      borderTop: `1px solid ${theme.color.grey.border}`,
      boxShadow: '0px 5px 8px rgba(114, 114, 114, 0.16), 0px 7px 14px rgba(214, 214, 214, 0.26)',
    },
  })();
};

const IncomingVolunteerViewApplicantScreen = ({
  selectedApplicationId,
  goBack,
  reloadList,
}: {
  selectedApplicationId: string;
  goBack: () => void;
  reloadList?: () => void;
}) => {
  const { theme, isMobile, isSm } = useCampfireTheme();
  const [leftMenuItem, setLeftMenuItem] = useState<LeftMenuItemType>();
  const [openBypass, setOpenBypass] = React.useState(false);
  const { data, loading } = useCampfireQuery<
    GetIncomingVolunteerApplicationDetail,
    GetIncomingVolunteerApplicationDetailVariables
  >(GET_INCOMING_VOLUNTEER_APPLICATION_DETAIL, {
    options: {
      variables: {
        applicationId: selectedApplicationId,
      },
    },
  });

  const application = useMemo(() => {
    return data?.vm.application ?? undefined;
  }, [data]);

  const invitation = application?.invitee;
  const isApplicationApproved = application && application?.status === 'approved';
  const statusChips = getIncomingStatusChips({
    applicationStatus: application?.status,
    profileId: application?.profile?.profileId,
    inviteeId: application?.invitee?.inviteeId,
    dateRevoked: application?.invitee?.dateRevoked,
    dateRemoved: application?.profile?.dateRemoved,
  });
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const classes = useStyles();

  const { runRemoveApplicant, runRemoveApplicantLoading, runApproveApplication } = useIncomingVolunteerActions();
  const { setRemindDialog, setApplicationsReminder } = useVolunteersContext();

  const { findRuleForRoles } = useMatchingRules();

  const matchingRuleMatch = useMemo(() => findRuleForRoles(application?.applicationRoles), [
    findRuleForRoles,
    application,
  ]);

  const onClickSendReminder = () => {
    if (application) {
      setApplicationsReminder([application]);
    }
    setRemindDialog(true);
  };

  const onBypass = () => {
    if (application) {
      runApproveApplication({
        applicationId: application.applicationId,
        status: 'bypassed',
      }).then(() => {
        if (reloadList) reloadList();
        goBack();
      });
    }
  };

  const isApplicationRemoved = application?.status === 'rejected' || application?.status === 'removed';
  const isAwaitingScreening = isAwaitingScreeningReview(application?.status);
  const [communicationsReloadSignal, setCommunicationsReloadSignal] = useState(0);

  const sidebarContent = application && (
    <Box width='100%' display='flex' flexDirection='column' flexGrow={1} style={{ gap: '1rem', minHeight: 0 }}>
      <ExpandableSidePanel
        fill
        expandable
        title='Comments'
        subtitle={`Comments left on ${application.name}'s application`}
      >
        <CommentsList applicationId={application.applicationId} collapsibleComposer />
      </ExpandableSidePanel>
      <ApplicantCommunicationsSection
        userId={application.profile?.userId}
        applicantName={application.name}
        reminders={application.applicationReminders ?? []}
        reloadSignal={communicationsReloadSignal}
      />
    </Box>
  );

  return (
    <IncomingVolunteersPageWrapper>
      {application && (
        <ConfirmationDialog
          title={'Confirm to remove applicant?'}
          body={
            <>
              <Typography variant='body1' gutterBottom>
                {`All onboarding progress will be removed for the applicant and their account will be deactivated.`}
              </Typography>
              <Typography variant='body1'>
                {
                  'Removed applicants will receive an email notifying them that their application has been unsuccessful.'
                }
              </Typography>
            </>
          }
          open={openConfirmation}
          closeActionText={'Cancel'}
          confirmActionText={'Remove Applicant'}
          handleCloseClick={() => setOpenConfirmation(false)}
          handleConfirmClick={() => {
            runRemoveApplicant({
              applicationId: application.applicationId,
              status: 'removed',
            }).then(() => {
              if (reloadList) reloadList();
              goBack();
            });
          }}
        />
      )}

      <CircularProgressOverlay isLoading={runRemoveApplicantLoading || loading} />
      <IncomingVolunteersLeftColumnWrapper rightColumnOpen={!!leftMenuItem}>
        {application && (
          <Grid item container direction='row' wrap='nowrap' alignItems='flex-start' className={classes.listItem}>
            <Grid item>
              <IconButton size='small' onClick={() => goBack()} style={{ padding: 6 }}>
                <KeyboardArrowLeft fontSize='small' />
              </IconButton>
            </Grid>
            <Grid item className={classes.iconContainer}>
              <FallbackAvatar
                initials={application?.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')}
                size={32}
              />
            </Grid>
            <Grid
              item
              container
              direction={isMobile ? 'column' : 'row'}
              wrap={isMobile ? 'wrap' : 'nowrap'}
              spacing={isMobile ? 1 : 0}
              alignItems='center'
            >
              <Grid item container direction='row' alignItems='center' spacing={1} style={{ flexGrow: 1 }}>
                <Grid item>
                  <Typography style={{ fontWeight: 700, color: theme.color.grey.neutralBrand800 }}>
                    {application?.name}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant='body2' color='textSecondary'>
                    {application?.email}
                  </Typography>
                </Grid>
                {statusChips.map((chip) => (
                  <Grid item key={chip.label}>
                    <StatusChip tiny status={chip.label} bgcolor={chip.color} />
                  </Grid>
                ))}
                {!isApplicationRemoved && (
                  <React.Fragment>
                    <Grid item>
                      <Typography variant='caption' color='textSecondary'>
                        {`Submitted ${formatDistance(unpackToDateTime(application?.dateSubmitted))}`}
                      </Typography>
                    </Grid>
                    {application?.mostRecentReminder && (
                      <Grid item>
                        <Typography variant='caption' color='textSecondary'>
                          {`Reminded ${formatDistance(unpackToDateTime(application.mostRecentReminder.remindedAt))}`}
                        </Typography>
                      </Grid>
                    )}
                    {application?.funnel && (
                      <Grid item style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Typography
                          variant='caption'
                          color='textSecondary'
                        >{`Funnel: ${application.funnel.name}`}</Typography>
                        <FunnelIndicators funnel={application.funnel} />
                      </Grid>
                    )}
                  </React.Fragment>
                )}
              </Grid>
              {!leftMenuItem ? (
                <Grid
                  container
                  style={{
                    display: 'flex',
                    gap: '12px',
                    width: 'auto',
                    flexShrink: 0,
                    flexWrap: isMobile ? 'wrap' : 'nowrap',
                  }}
                  justify={isMobile ? 'flex-start' : 'flex-end'}
                >
                  {!isApplicationRemoved && (
                    <Grid item>
                      <CommunicateButton
                        profile={
                          application?.profile && {
                            profileId: application.profile.profileId,
                            preferredName: application.profile.preferredName,
                            lastName: application.profile.lastName,
                            contactNumber: application.profile.contactNumber,
                            avatarUrl: application.profile.avatarUrl,
                          }
                        }
                        dataTrack='fs-incVol-applicant-contact-button'
                        buttonStyle={{ marginRight: 0 }}
                        onSendReminder={isAwaitingScreening ? undefined : onClickSendReminder}
                        onSent={() => setCommunicationsReloadSignal((x) => x + 1)}
                      />
                    </Grid>
                  )}
                  {application?.status !== 'approved' && !isApplicationRemoved && (
                    <Grid item>
                      <TabletButton
                        variant='outlined'
                        onClick={() => setOpenBypass(true)}
                        style={{ borderRadius: 4, padding: '0.625rem 1.125rem', whiteSpace: 'nowrap' }}
                      >
                        <Redo color='inherit' style={{ fontSize: 14 }} />
                        <Typography noWrap style={{ fontSize: 14, marginLeft: 8, fontWeight: 500 }}>
                          Bypass
                        </Typography>
                      </TabletButton>
                    </Grid>
                  )}
                  {!isApplicationRemoved && (
                    <Grid item>
                      <TabletButton
                        color='error'
                        variant='outlined'
                        onClick={() => setOpenConfirmation(true)}
                        style={{ borderRadius: 4, padding: '0.625rem 1.125rem', whiteSpace: 'nowrap' }}
                      >
                        <DeleteIcon color='inherit' style={{ fontSize: 14 }} />
                        <Typography noWrap style={{ fontSize: 14, marginLeft: 8, fontWeight: 500 }}>
                          {isSm ? 'Remove' : 'Remove Applicant'}
                        </Typography>
                      </TabletButton>
                    </Grid>
                  )}
                </Grid>
              ) : null}
            </Grid>
          </Grid>
        )}
        <Box className={classes.canvas}>
          <Grid container spacing={2} alignItems='stretch' style={{ flexGrow: 1 }}>
            <Grid item xs={12} md={!leftMenuItem && application ? 8 : 12}>
              <Box className={classes.card}>
                {invitation ? (
                  <IncomingVolunteersInviteTaskSection
                    subText={`Invited by ${invitation.invitedBy.profile.preferredName} ${invitation.invitedBy.profile.lastName}`}
                    status={`Invited ${formatDistance(unpackToDateTime(invitation.dateInvited))}`}
                    onClick={() => setLeftMenuItem({ inviteeId: invitation.inviteeId, type: invitation.__typename })}
                  />
                ) : null}

                {application ? (
                  <IncomingVolunteersApplicationTaskSection
                    status={isApplicationApproved ? 'approved' : 'pending'}
                    taskRecentActivityStatus={`Submitted ${formatDistance(
                      unpackToDateTime(application?.dateSubmitted)
                    )}`}
                    onClick={() =>
                      setLeftMenuItem({
                        applicationId: application?.applicationId,
                        type: application?.__typename,
                      })
                    }
                  />
                ) : null}
                {isApplicationApproved ? (
                  <IncomingVolunteerTasksSection
                    setLeftMenuItem={setLeftMenuItem}
                    userId={application?.profile?.userId ?? ''}
                  />
                ) : null}

                {matchingRuleMatch && application && application.profile?.profileId && (
                  <MatchingPanel
                    applicantName={application.name}
                    applicantRoleName={matchingRuleMatch.matchedRole.name}
                    applicantProfileId={application.profile.profileId}
                    counterpartRoleId={matchingRuleMatch.suggestedRole.activityRoleId}
                    counterpartRoleName={matchingRuleMatch.suggestedRole.name}
                    ruleProgramId={matchingRuleMatch.rule.program?.programId ?? null}
                    minimumScore={matchingRuleMatch.rule.minimumScore}
                  />
                )}
              </Box>
            </Grid>
            {!leftMenuItem && application && (
              <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                {sidebarContent}
              </Grid>
            )}
          </Grid>
        </Box>
      </IncomingVolunteersLeftColumnWrapper>

      {leftMenuItem ? (
        <IncomingVolunteersRightColumnWrapper>
          <TaskSideMenuBar
            onClose={() => setLeftMenuItem(undefined)}
            leftMenuItem={leftMenuItem}
            reload={reloadList}
            selectedUserId={application?.profile?.userId}
          />
        </IncomingVolunteersRightColumnWrapper>
      ) : null}
      <BypassDialog
        open={openBypass}
        onClose={() => setOpenBypass(false)}
        onCancel={() => setOpenBypass(false)}
        onBypass={onBypass}
      />
    </IncomingVolunteersPageWrapper>
  );
};

export { IncomingVolunteerViewApplicantScreen };
